import React, { useState, useMemo } from 'react';
import { useApiQuery, useApiMutation } from '../hooks/useApi';
import { apiClient } from '../utils/apiClient';
import { useToast } from '../contexts/ToastContext';
import ErrorState from '../components/ErrorState';
import './Gear.css';

interface GearItem {
  gearId: string;
  name: string;
  category: string;
  weight?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface GearFormData {
  name: string;
  category: string;
  weight: string;
  notes: string;
}

const CATEGORIES = [
  { value: 'shelter', label: 'Shelter' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'cooking', label: 'Cooking' },
  { value: 'food', label: 'Food' },
  { value: 'water', label: 'Water' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'safety', label: 'Safety' },
  { value: 'first_aid', label: 'First Aid' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'hygiene', label: 'Hygiene' },
  { value: 'tools', label: 'Tools' },
  { value: 'recreation', label: 'Recreation' },
  { value: 'other', label: 'Other' },
];

const Gear: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingGear, setEditingGear] = useState<GearItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [formData, setFormData] = useState<GearFormData>({
    name: '',
    category: '',
    weight: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<GearFormData>>({});

  // Hooks
  const { showSuccess, showError } = useToast();

  // API hooks
  const { 
    data: gearItems, 
    loading, 
    error, 
    refetch, 
    isRetrying, 
    retryCount,
    isOffline 
  } = useApiQuery<GearItem[]>(
    () => apiClient.getGear(),
    []
  );

  const { mutate: createGear, loading: creating, error: createError } = useApiMutation<GearItem>();
  const { mutate: updateGear, loading: updating, error: updateError } = useApiMutation<GearItem>();
  const { mutate: deleteGear, loading: deleting, error: deleteError } = useApiMutation<void>();

  // Filter gear items based on search and category
  const filteredGear = useMemo(() => {
    if (!gearItems) return [];

    return gearItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || item.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [gearItems, searchTerm, selectedCategory]);

  // Form validation
  const validateForm = (): boolean => {
    const errors: Partial<GearFormData> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.category) {
      errors.category = 'Category is required';
    }

    if (formData.weight && isNaN(Number(formData.weight))) {
      errors.weight = 'Weight must be a number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const gearData = {
      name: formData.name.trim(),
      category: formData.category,
      weight: formData.weight ? Number(formData.weight) : undefined,
      notes: formData.notes.trim() || undefined,
    };

    let success = false;

    if (editingGear) {
      success = await updateGear(() => apiClient.updateGear(editingGear.gearId, gearData));
      if (success) {
        showSuccess('Gear Updated', `${gearData.name} has been updated successfully.`);
      } else if (updateError) {
        showError('Update Failed', updateError);
      }
    } else {
      success = await createGear(() => apiClient.createGear(gearData));
      if (success) {
        showSuccess('Gear Added', `${gearData.name} has been added to your inventory.`);
      } else if (createError) {
        showError('Creation Failed', createError);
      }
    }

    if (success) {
      handleCloseForm();
      refetch();
    }
  };

  // Handle gear deletion
  const handleDelete = async (gearId: string) => {
    const gearName = gearItems?.find(g => g.gearId === gearId)?.name || 'gear item';
    const success = await deleteGear(() => apiClient.deleteGear(gearId));
    
    if (success) {
      setShowDeleteConfirm(null);
      showSuccess('Gear Deleted', `${gearName} has been removed from your inventory.`);
      refetch();
    } else if (deleteError) {
      showError('Deletion Failed', deleteError);
    }
  };

  // Form management
  const handleOpenForm = (gear?: GearItem) => {
    if (gear) {
      setEditingGear(gear);
      setFormData({
        name: gear.name,
        category: gear.category,
        weight: gear.weight?.toString() || '',
        notes: gear.notes || '',
      });
    } else {
      setEditingGear(null);
      setFormData({
        name: '',
        category: '',
        weight: '',
        notes: '',
      });
    }
    setFormErrors({});
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingGear(null);
    setFormData({
      name: '',
      category: '',
      weight: '',
      notes: '',
    });
    setFormErrors({});
  };

  const handleInputChange = (field: keyof GearFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (loading && !isRetrying) {
    return (
      <div className="gear-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your gear inventory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const getErrorIcon = () => {
      if (isOffline) return 'offline';
      if (error.includes('network') || error.includes('connection')) return 'network';
      return 'error';
    };

    return (
      <div className="gear-page">
        <ErrorState
          title="Failed to load gear inventory"
          message={error}
          onRetry={refetch}
          retryLoading={isRetrying}
          retryLabel={retryCount > 0 ? `Retry (${retryCount}/3)` : 'Try Again'}
          icon={getErrorIcon()}
          size="large"
        />
      </div>
    );
  }

  return (
    <div className="gear-page">
      <div className="page-header">
        <div>
          <h1>Gear Inventory</h1>
          <p>Manage your camping and travel equipment</p>
          {isRetrying && (
            <div className="retry-indicator">
              <div className="retry-spinner"></div>
              <span>Retrying... ({retryCount}/3)</span>
            </div>
          )}
        </div>
        <button 
          className="primary-button" 
          onClick={() => handleOpenForm()}
          disabled={isOffline}
          title={isOffline ? 'Cannot add gear while offline' : 'Add new gear item'}
        >
          Add New Gear
        </button>
      </div>

      <div className="gear-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search gear..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="filter-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(category => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>
      </div>

      <div className="gear-grid">
        {filteredGear.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎒</div>
            {gearItems?.length === 0 ? (
              <>
                <h3>No gear items yet</h3>
                <p>Start building your gear inventory by adding your first item.</p>
                <button className="primary-button" onClick={() => handleOpenForm()}>
                  Add Your First Gear Item
                </button>
              </>
            ) : (
              <>
                <h3>No matching gear found</h3>
                <p>Try adjusting your search or filter criteria.</p>
                <button className="secondary-button" onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}>
                  Clear Filters
                </button>
              </>
            )}
          </div>
        ) : (
          filteredGear.map(gear => (
            <div key={gear.gearId} className="gear-card">
              <div className="gear-card-header">
                <h3 className="gear-name">{gear.name}</h3>
                <div className="gear-actions">
                  <button
                    className="action-button edit-button"
                    onClick={() => handleOpenForm(gear)}
                    title="Edit gear"
                  >
                    ✏️
                  </button>
                  <button
                    className="action-button delete-button"
                    onClick={() => setShowDeleteConfirm(gear.gearId)}
                    title="Delete gear"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div className="gear-details">
                <div className="gear-category">
                  <span className={`category-badge category-${gear.category}`}>
                    {CATEGORIES.find(c => c.value === gear.category)?.label || gear.category}
                  </span>
                </div>
                
                {gear.weight && (
                  <div className="gear-weight">
                    <span className="detail-label">Weight:</span>
                    <span className="detail-value">{gear.weight}g</span>
                  </div>
                )}
                
                {gear.notes && (
                  <div className="gear-notes">
                    <span className="detail-label">Notes:</span>
                    <p className="detail-value">{gear.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Gear Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingGear ? 'Edit Gear Item' : 'Add New Gear Item'}</h2>
              <button className="close-button" onClick={handleCloseForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="gear-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={formErrors.name ? 'error' : ''}
                  placeholder="Enter gear name"
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className={formErrors.category ? 'error' : ''}
                >
                  <option value="">Select a category</option>
                  {CATEGORIES.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {formErrors.category && <span className="error-message">{formErrors.category}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="weight">Weight (grams)</label>
                <input
                  type="number"
                  id="weight"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  className={formErrors.weight ? 'error' : ''}
                  placeholder="Enter weight in grams"
                  min="0"
                  step="1"
                />
                {formErrors.weight && <span className="error-message">{formErrors.weight}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Add any additional notes about this gear item"
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="secondary-button" onClick={handleCloseForm}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={creating || updating}
                >
                  {creating || updating ? 'Saving...' : (editingGear ? 'Update Gear' : 'Add Gear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Gear Item</h2>
              <button className="close-button" onClick={() => setShowDeleteConfirm(null)}>×</button>
            </div>
            
            <div className="delete-content">
              <p>Are you sure you want to delete this gear item? This action cannot be undone.</p>
              <p className="gear-name-confirm">
                <strong>{gearItems?.find(g => g.gearId === showDeleteConfirm)?.name}</strong>
              </p>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="secondary-button" 
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="danger-button"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Gear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gear;